import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, ChevronDown, ChevronUp, FileText, Loader2,
} from "lucide-react";
import ConfidenceBadge from "@/components/business/ConfidenceBadge";
import RiskBadge from "@/components/business/RiskBadge";

/**
 * @typedef {Object} ExtractionCardProps
 * @property {Object} extraction - extraction record from the review queue
 * @property {() => void} onDone - called after approve or reject to refresh the parent
 */

/**
 * Per-document extraction review card, extracted verbatim from AISIngestion.jsx.
 * Handles field-level approval, reconciliation check display, and reviewer notes.
 * @param {ExtractionCardProps} props
 */
export default function ExtractionCard({ extraction, onDone }) {
  const fields = (extraction.extracted && extraction.extracted.fields) || {};
  const rowKeys = Object.keys(fields).filter(k => {
    const v = fields[k]?.value;
    return v !== null && v !== undefined && v !== "" && v !== 0;
  });
  const [approved, setApproved] = useState(new Set(rowKeys));
  const [expanded, setExpanded] = useState(true);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const rec = extraction.reconciliation || {};

  const toggle = (k) => {
    const n = new Set(approved);
    n.has(k) ? n.delete(k) : n.add(k);
    setApproved(n);
  };

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/ingestion/review/${extraction.id}/approve`, {
        approved_field_keys: Array.from(approved),
        reviewer_note: note || undefined,
      });
      toast.success("Approved — filing data prefilled");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await api.post(`/ingestion/review/${extraction.id}/reject`, {
        approved_field_keys: [],
        reviewer_note: note || undefined,
      });
      toast("Extraction rejected", { description: "Kept in history for audit" });
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded border border-border bg-background">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-muted/40" data-testid={`extraction-${extraction.id.slice(0, 6)}`}>
        <div className="flex items-center gap-3">
          <FileText className="h-4 w-4 text-primary" />
          <div className="text-left">
            <div className="font-semibold uppercase">{extraction.doc_type} v{extraction.version}</div>
            <div className="text-[11px] text-muted-foreground">
              {extraction.ai_provider}/{extraction.ai_model} · prompt {extraction.prompt_version} · {new Date(extraction.created_at).toLocaleString()}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {rec.summary && <>
            {rec.summary.critical > 0 && <Badge className="bg-rose-500/10 text-rose-700 border-rose-300 border">{rec.summary.critical} critical</Badge>}
            {rec.summary.warnings > 0 && <Badge className="bg-amber-500/10 text-amber-700 border-amber-300 border">{rec.summary.warnings} warn</Badge>}
            <Badge className="bg-emerald-500/10 text-emerald-700 border-emerald-300 border">{rec.summary.matched} matched</Badge>
          </>}
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border p-4 space-y-4">
          {rec.recommendation && (
            <div className="rounded border border-border bg-muted/40 p-3 text-xs">
              <div className="font-semibold text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Recommendation</div>
              <div className="mt-1">{rec.recommendation}</div>
            </div>
          )}

          {(rec.checks || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Reconciliation vs filing</div>
              <div className="mt-2 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40"><tr>
                    <th className="text-left p-2">Label</th>
                    <th className="text-right p-2">AIS</th>
                    <th className="text-right p-2">Declared</th>
                    <th className="text-right p-2">Δ</th>
                    <th className="text-left p-2">Severity</th>
                  </tr></thead>
                  <tbody>
                    {rec.checks.map((c, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="p-2">{c.label}</td>
                        <td className="p-2 text-right font-mono">₹{Math.round(c.reported).toLocaleString()}</td>
                        <td className="p-2 text-right font-mono">₹{Math.round(c.declared).toLocaleString()}</td>
                        <td className={`p-2 text-right font-mono ${c.diff > 0 ? "text-rose-700" : c.diff < 0 ? "text-amber-700" : ""}`}>
                          {c.diff === 0 ? "—" : `₹${Math.round(Math.abs(c.diff)).toLocaleString()}`}
                        </td>
                        <td className="p-2"><RiskBadge severity={c.severity} className="text-[10px]" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Fields to approve</div>
              <div className="text-xs text-muted-foreground">{approved.size}/{rowKeys.length} selected</div>
            </div>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
              {rowKeys.map(k => {
                const f = fields[k] || {};
                const conf = Math.round((f.confidence || 0) * 100);
                const isNum = typeof f.value === "number";
                return (
                  <label key={k} className={`flex items-start gap-2 rounded border p-2 text-xs cursor-pointer ${approved.has(k) ? "border-primary bg-primary/5" : "border-border"}`}>
                    <input type="checkbox" checked={approved.has(k)} onChange={() => toggle(k)} className="mt-0.5" data-testid={`field-${k}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold">{k}</div>
                      <div className="font-mono">{isNum ? `₹${(f.value || 0).toLocaleString()}` : String(f.value || "—")}</div>
                      <div className="mt-0.5 text-[10px] text-muted-foreground">
                        Src: {f.source} {f.page ? `· p.${f.page}` : ""}
                      </div>
                    </div>
                    <ConfidenceBadge value={conf} />
                  </label>
                );
              })}
              {rowKeys.length === 0 && <div className="text-xs text-muted-foreground col-span-2">No non-zero fields extracted.</div>}
            </div>
          </div>

          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reviewer note (optional)" className="min-h-[60px]" />
          <div className="flex flex-wrap justify-end gap-2">
            <Button onClick={reject} disabled={busy} variant="outline" data-testid={`reject-${extraction.id.slice(0, 6)}`}><XCircle className="h-4 w-4 mr-1.5" />Reject</Button>
            <Button onClick={approve} disabled={busy || approved.size === 0} data-testid={`approve-${extraction.id.slice(0, 6)}`}>
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
              Approve &amp; prefill
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
